import axios, { AxiosError, AxiosInstance, AxiosRequestHeaders, InternalAxiosRequestConfig } from "axios";
import { JwtStore } from "./JwtStore";
import { isTerminalRefreshError, RefreshCoordinator, SESSION_EVICTED_REASON } from "./RefreshCoordinator";

/**
 * sessionStorage key that carries a session-eviction sign-out across the hard
 * page reload the terminal-auth redirect triggers. Written just before the
 * redirect and consumed once by the /app landing surface, which shows the
 * sign-out message. sessionStorage (not localStorage) so the notice dies with
 * the tab and never resurfaces in an unrelated later session.
 */
export const SESSION_EVICTION_STORAGE_KEY = "session-eviction-notice";

/**
 * Best-effort persist — private-mode or a storage-disabled browser must not
 * throw and abort the sign-out redirect; the message is simply skipped then.
 */
function persistEvictionNotice(): void {
  try {
    sessionStorage.setItem(SESSION_EVICTION_STORAGE_KEY, "1");
  } catch {
    // Storage unavailable: proceed with the redirect, just without the message.
  }
}

/**
 * Reads and clears the one-shot eviction notice, returning true at most once
 * per stored notice. Storage errors read as "no notice".
 */
export function consumeEvictionNotice(): boolean {
  try {
    if (sessionStorage.getItem(SESSION_EVICTION_STORAGE_KEY) === null) {
      return false;
    }
    sessionStorage.removeItem(SESSION_EVICTION_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Silent-fail carve-out — see plan R3 and analysis Q5.
 * BE source: backend/player-rest-service/.../UserController.java line ~321 (getAccountBalancesAndBonuses).
 * If the BE endpoint is renamed, search-and-update here.
 *
 * When a 401 surfaces from this endpoint AND refresh terminally fails,
 * we clear the JWT but DO NOT redirect — preserves today's UX where
 * balance polling failures do not eject the user from the page.
 */
const BALANCE_ENDPOINT_PATH = "/user/account-balances-and-bonuses";

/**
 * Silent-fail carve-out — see NS-1938 AC#7.
 * BE source: backend/player-rest-service/.../ZendeskController.java.
 *
 * When a 401 surfaces from this endpoint AND refresh terminally fails,
 * we clear the JWT but DO NOT redirect — the widget falls back to
 * anonymous/guest chat mode per AC#7 ("widget continues, NOT hidden, NOT crashed").
 */
const ZENDESK_JWT_ENDPOINT_PATH = "/user/zendesk/jwt";

/**
 * Marker on the axios request config used by the response interceptor
 * to detect requests that have already been retried after a refresh.
 * Prevents an infinite refresh→401→refresh loop if the BE keeps 401-ing
 * even after a fresh JWT.
 */
interface RetryFlaggedConfig extends InternalAxiosRequestConfig {
  __retriedAfterRefresh?: boolean;
}

export class HttpService {
  axios: AxiosInstance;
  lastRequest: number;
  lastSuccessfulRequest: number;
  // True only in SESSION auth-mode, where identity normally lives solely in the
  // HttpOnly session cookie and no refresh token is issued — so a 401 without a
  // held JWT credential cannot be recovered by refreshing and is already terminal.
  // Set from GET /settings once the mode is known (see the load bootstrap).
  private sessionAuth = false;
  constructor(
    private baseUrl: string,
    private jwtStore: JwtStore,
    private refreshCoordinator: RefreshCoordinator,
  ) {
    this.lastRequest = Date.now();
    this.lastSuccessfulRequest = Date.now();
    this.axios = axios.create({
      headers: {
        "Content-type": "application/json",
      },
    });
    this.axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const jwt = this.jwtStore.getJwt();
      if (jwt && typeof config.url === "string" && config.url.startsWith(this.baseUrl)) {
        config.headers.set("Authorization", `Bearer ${jwt}`);
      }
      return config;
    });
    this.axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => this.handleResponseError(error),
    );
  }

  /**
   * Will return a string copy of the baseUrl
   */
  getBaseUrl(): string {
    return `${this.baseUrl}`;
  }

  setSessionAuth(sessionAuth: boolean | undefined): void {
    // Strict: only an explicit true enables the SESSION short-circuit, so an
    // absent or unknown mode keeps the refresh flow rather than logging DUAL
    // players out on their first 401.
    this.sessionAuth = sessionAuth === true;
  }
  getTimeSinceLastRequest(): number {
    return Date.now() - this.lastRequest;
  }
  getTimeSinceLastSuccessfulRequest(): number {
    return Date.now() - this.lastSuccessfulRequest;
  }

  get<R>(url: string, headers?: AxiosRequestHeaders): Promise<R> {
    this.lastRequest = Date.now();
    return new Promise((resolve, reject) => {
      this.axios
        .get(this.baseUrl + url, { withCredentials: true, headers: headers })
        .then((response) => {
          this.lastSuccessfulRequest = Date.now();
          resolve(response.data);
        })
        .catch((e) => this.handleError(url, e, reject));
    });
  }
  getWithSite<R>(url: string, headers?: AxiosRequestHeaders): Promise<{ response: R; siteNameId: string }> {
    this.lastRequest = Date.now();
    return new Promise((resolve, reject) => {
      this.axios
        .get(this.baseUrl + url, { withCredentials: true, headers: headers })
        .then((response) => {
          this.lastSuccessfulRequest = Date.now();
          resolve({ response: response.data, siteNameId: response?.headers?.["x-site-name-id"] ?? "default" });
        })
        .catch((e) => this.handleError(url, e, reject));
    });
  }
  post<R, E>(url: string, entity: E, headers?: AxiosRequestHeaders): Promise<R> {
    this.lastRequest = Date.now();
    return new Promise((resolve, reject) => {
      this.axios
        .post(this.baseUrl + url, entity, { withCredentials: true, headers: headers })
        .then((response) => {
          this.lastSuccessfulRequest = Date.now();
          resolve(response.data);
        })
        .catch((e) => this.handleError(url, e, reject));
    });
  }
  put<R, E = unknown>(url: string, entity?: E, headers?: AxiosRequestHeaders): Promise<R> {
    this.lastRequest = Date.now();
    return new Promise((resolve, reject) => {
      this.axios
        .put(this.baseUrl + url, entity ?? {}, { withCredentials: true, headers: headers })
        .then((response) => {
          this.lastSuccessfulRequest = Date.now();
          resolve(response.data);
        })
        .catch((e) => this.handleError(url, e, reject));
    });
  }
  delete<R>(url: string): Promise<R> {
    this.lastRequest = Date.now();
    return new Promise((resolve, reject) => {
      this.axios
        .delete(this.baseUrl + url, { withCredentials: true })
        .then((response) => {
          this.lastSuccessfulRequest = Date.now();
          resolve(response.data);
        })
        .catch((e) => this.handleError(url, e, reject));
    });
  }
  beacon(url: string): boolean {
    if (!navigator.sendBeacon) {
      return false;
    }
    return navigator.sendBeacon(this.baseUrl + url);
  }

  head(url: string, headers?: AxiosRequestHeaders): Promise<void> {
    this.lastRequest = Date.now();
    return new Promise((resolve, reject) => {
      this.axios
        .head(this.baseUrl + url, { withCredentials: true, headers: headers })
        .then(() => {
          this.lastSuccessfulRequest = Date.now();
          resolve();
        })
        .catch((e) => this.handleError(url, e, reject));
    });
  }

  /**
   * Opens a Server-Sent Events stream and invokes onEvent per event until the stream ends or the
   * signal aborts. Uses fetch rather than the native EventSource because EventSource cannot send an
   * Authorization header — this mirrors the axios request interceptor (Bearer when a JWT is held,
   * cookies via credentials), so the stream authenticates the same way in every AuthMode.
   */
  async openEventStream(url: string, onEvent: (name: string, data: string) => void, signal: AbortSignal): Promise<void> {
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    const jwt = this.jwtStore.getJwt();
    if (jwt) {
      headers.Authorization = `Bearer ${jwt}`;
    }
    const response = await fetch(this.baseUrl + url, { headers, credentials: "include", signal });
    if (!response.ok || !response.body) {
      throw new Error(`Event stream ${url} failed with status ${response.status}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let streaming = true;
    while (streaming) {
      const { value, done } = await reader.read();
      if (done) {
        streaming = false;
        break;
      }
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = HttpService.parseEventFrame(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        if (frame) {
          onEvent(frame.name, frame.data);
        }
        boundary = buffer.indexOf("\n\n");
      }
    }
  }

  private static parseEventFrame(raw: string): { name: string; data: string } | undefined {
    let name = "message";
    const dataLines: string[] = [];
    for (const line of raw.split("\n")) {
      if (line.startsWith(":")) {
        continue; // Comment line, e.g. the keep-alive heartbeat.
      }
      if (line.startsWith("event:")) {
        name = line.slice("event:".length).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).replace(/^ /, ""));
      }
    }
    return dataLines.length ? { name, data: dataLines.join("\n") } : undefined;
  }

  shouldRedirectToAppLogin(url: string) {
    const token = localStorage.getItem("token");
    const salt = localStorage.getItem("salt");
    if (!token || !salt) {
      return false;
    }
    if (url.indexOf("/user/authenticate") != -1 || url.indexOf("/user/logout") != -1 || url.endsWith("/user/zendesk/jwt")) {
      return false;
    }
    return true;
  }
  /**
   * Response-interceptor handler. Runs BEFORE the per-method
   * .catch(handleError) chain. Behaviour:
   *
   * - 401 from a player-rest call → trigger RefreshCoordinator. On
   *   success, replay the original request (request interceptor will
   *   pick up the new JWT). On terminal failure: redirect to login,
   *   except for the silent-fail carve-outs (balance polling per R3,
   *   Zendesk JWT mint per NS-1938 AC#7).
   * - 401 from a non-player-rest URL (CDN, Contentful, etc.) →
   *   pass through unchanged.
   * - Already-retried request that gets another 401 → pass through
   *   (avoid infinite loop).
   * - Any non-401 → pass through to per-method handleError.
   */
  private async handleResponseError(error: AxiosError): Promise<never | unknown> {
    const status = error.response?.status;
    const config = error.config as RetryFlaggedConfig | undefined;
    const url = config?.url;

    // url.startsWith(this.baseUrl) is the gate that scopes refresh-on-401 to
    // player-rest only. It only works because this.axios.create() does NOT set
    // a baseURL — every request is made with the absolute `this.baseUrl + url`
    // we construct in get/post/etc. If a future change adds `baseURL` to the
    // axios config, axios will strip the base from `config.url` and this gate
    // will silently stop matching. Keep the manual baseUrl concatenation, or
    // update this gate to use the full URL via `axios.getUri(config)`.
    if (status !== 401 || !config || typeof url !== "string" || !url.startsWith(this.baseUrl)) {
      return Promise.reject(error);
    }
    if (this.sessionAuth && !this.jwtStore.holdsJwtCredential()) {
      // A session-authenticated player has no refresh token to trade, so the
      // refresh probe would itself 401 — the 401 is already terminal. Players
      // the server logged in with a JWT on a SESSION operator hold a credential
      // and take the refresh path below instead. Clearing the (empty) JWT keeps
      // the terminal contract identical to the refresh path's.
      this.jwtStore.clear();
      // Never an eviction here: that reason rides the refresh-token 401 body, which SESSION mode has
      // no token to present for — so this terminal redirect is always the ordinary kind.
      this.handleTerminalAuthRedirect(url, false);
      return Promise.reject(error);
    }
    if (config.__retriedAfterRefresh) {
      // Already retried after refresh and still 401 — terminal, but the
      // refresh itself succeeded. Treat as a hard logout (never an eviction:
      // an eviction surfaces as a terminal refresh failure, not a replay 401).
      this.jwtStore.clear();
      this.handleTerminalAuthRedirect(url, false);
      return Promise.reject(error);
    }

    try {
      await this.refreshCoordinator.refresh();
    } catch (refreshError) {
      if (isTerminalRefreshError(refreshError)) {
        // RefreshCoordinator has already cleared the JWT. Decide redirect.
        const evicted = refreshError.reason === SESSION_EVICTED_REASON;
        this.handleTerminalAuthRedirect(url, evicted);
      }
      return Promise.reject(error);
    }

    // Refresh succeeded — replay the original request once.
    config.__retriedAfterRefresh = true;
    return this.axios.request(config);
  }

  private handleTerminalAuthRedirect(url: string, evicted: boolean): void {
    if (this.isSilentFailCarveOut(url)) {
      // Silent-fail terminal: caller's .catch handles state cleanup.
      return;
    }
    if (this.shouldRedirectToAppLogin(url)) {
      if (evicted) {
        // Stash only on the branch that actually navigates to /app: that reload wipes in-memory state, so
        // the /app surface reads the reason after remount. Persisting before this check would leave a
        // one-shot notice behind on the in-place reload path, to surface out of context on a later /app visit.
        persistEvictionNotice();
      }
      // window.location === document.location; use window so tests can stub it.
      window.location.pathname = "/app";
      return;
    }
    window.location.reload();
  }

  private isSilentFailCarveOut(url: string): boolean {
    return url.endsWith(BALANCE_ENDPOINT_PATH) || url.endsWith(ZENDESK_JWT_ENDPOINT_PATH);
  }

  /**
   * NS-1864: 401s on player-rest are caught by the response interceptor
   * (handleResponseError above), which owns refresh, retry, and redirect.
   * This handler only forwards the error to the caller — kept for non-401
   * errors and any future per-method post-processing that's not interceptor-shaped.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleError(_url: string, e: AxiosError, reject: (reason?: unknown) => void) {
    reject(e);
  }
}
